/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProgramPanel } from 'contexts/programPanelContext';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programsApi } from 'api';
import { UpdateProgramSettingsParametersPayload } from 'types/Program';
import { Link } from 'components/_new/Link';
import { Icon } from 'components/_new/Icon';
import { useAuth } from 'contexts/authContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useScope } from 'contexts/scopeContext';
import { Panel, PanelItem } from '../../partials/panel';

type ProgramSettingsForm = {
	numberOfSessions: number;
	mentibotEnabled: boolean;
	requireTwoFactorAuth: boolean;
	mentorLabelFileId: number | null;
	menteeLabelFileId: number | null;
	mentorLabelKnowledgeBaseArticleId: number | null;
	menteeLabelKnowledgeBaseArticleId: number | null;
	contact: string | null;
	surveyNotifications: boolean;
	calendarApiIntegration: boolean;
	eventsEnabled: boolean;
	brandColor: string | null;
};

export const ProgramAdditionalSettings = () => {
	const { t } = useTranslation();

	const { currentUser } = useAuth();
	const { currentProgramRefetch } = useScope();
	const { data: programData, refetch: programRefetch, panelPath } = useProgramPanel();
	const editModeState = useState(false);
	const form = useForm<ProgramSettingsForm>({
		defaultValues: {
			numberOfSessions: programData?.numberOfSessions,
			mentibotEnabled: programData?.mentibotEnabled,
			requireTwoFactorAuth: programData?.requireTwoFactorAuth,
			mentorLabelKnowledgeBaseArticleId: programData?.mentorLabelKnowledgeBaseArticleId,
			menteeLabelKnowledgeBaseArticleId: programData?.menteeLabelKnowledgeBaseArticleId,
			contact: programData?.contact || null,
			surveyNotifications: programData?.surveyNotifications,
			calendarApiIntegration: programData?.calendarApiIntegration,
			eventsEnabled: programData?.eventsEnabled,
			brandColor: programData?.brandColor,
		},
	});

	const { mutateAsync, isLoading } = useMutation(
		(data: UpdateProgramSettingsParametersPayload) =>
			programsApi.putProgramAdminSettingsParameters(Number(programData?.id), data),
		{
			onSuccess: () => {
				programRefetch?.();
				currentProgramRefetch();
			},
		}
	);

	const handleSave = (values: ProgramSettingsForm) => {
		const { brandColor, ...restValues } = values;
		const data = {
			...restValues,
			brandColor: brandColor && brandColor?.length > 0 ? brandColor : null,
		};
		mutateAsync(data).then(() => {
			editModeState[1](false);
		});
	};

	const { data: labelFiles } = useQuery(
		[
			'KNOWLEDGE_BASE_LINKEDIN_BADGE_OPTIONS',
			{ programId: Number(programData?.id), language: currentUser?.language },
		],
		() => knowledgeBaseApi.program.getLinkedInBadge(Number(programData?.id)),
		{ enabled: true, initialData: [] }
	);

	return (
		<Panel
			title={t('programPanel.dashboard.additionalSettings.title')}
			form={form}
			editModeState={editModeState}
			isLoading={isLoading}
			onSave={handleSave}
		>
			<>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.contractInPair')}
					value={programData?.stats?.contractFieldCount}
					transformValue={({ value }) => (
						<div className="flex gap-2">
							<span>
								{t('programPanel.dashboard.additionalSettings.contractElements', {
									count: value,
								})}
							</span>

							<Link to={`/program/${programData?.name}/contract`}>
								<Icon name="pencil" iconSet="fa" iconStyle="solid" />
							</Link>
						</div>
					)}
					editable
					bold
				/>
			</>
			<>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.numberOfSessions')}
					value={programData?.numberOfSessions}
					type="number"
					controller={{ control: form.control, name: 'numberOfSessions' }}
					inputNumberProps={{ min: 3, max: 12 }}
					bold
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.labelPlaceholderForMentors')}
					value={programData?.mentorLabelKnowledgeBaseArticleId}
					type="select"
					controller={{ control: form.control, name: 'mentorLabelKnowledgeBaseArticleId' }}
					inputDropdownProps={{
						options: labelFiles.map(({ id, name }) => ({
							value: id,
							label: name,
						})),
						noOptionsText: t('programPanel.dashboard.additionalSettings.labelPlaceholderNoFiles'),
					}}
					withEmptyText="default"
					bold
					transformValue={({ value, label }) => {
						if (!value) {
							return null;
						}
						return (
							<>
								{label}
								<Link
									to={{ pathname: `/${panelPath}/knowledge-base/article/${value}` }}
									className="ml-2"
								>
									<Icon name="external-link" />
								</Link>
							</>
						);
					}}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.labelPlaceholderForMentees')}
					value={programData?.menteeLabelKnowledgeBaseArticleId}
					type="select"
					controller={{ control: form.control, name: 'menteeLabelKnowledgeBaseArticleId' }}
					inputDropdownProps={{
						options: labelFiles.map(({ id, name }) => ({
							value: id,
							label: name,
						})),
						noOptionsText: t('programPanel.dashboard.additionalSettings.labelPlaceholderNoFiles'),
					}}
					withEmptyText="default"
					bold
					transformValue={({ value, label }) => {
						if (!value) {
							return null;
						}
						return (
							<>
								{label}
								<Link
									to={{ pathname: `/${panelPath}/knowledge-base/article/${value}` }}
									className="ml-2"
								>
									<Icon name="external-link" />
								</Link>
							</>
						);
					}}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.programContact')}
					value={programData?.contact && programData?.contact.length > 0}
					transformValue="yes/no"
					editable
				/>
				<PanelItem
					type="string-editor"
					value={programData?.contact}
					controller={{ control: form.control, name: 'contact' }}
					visibleOnlyInEditMode
					// inputMarkdownEditorProps={{
					// 	uploadRequestFunction: (data: any) =>
					// 		programsApi.uploadProgramAdminContactEditorImage(Number(programData?.id), data),
					// }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.eventsEnabled')}
					value={programData?.eventsEnabled}
					type="boolean"
					transformValue="active/unactive"
					controller={{ control: form.control, name: 'eventsEnabled' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.mentibotEnabled')}
					value={programData?.mentibotEnabled}
					type="boolean"
					transformValue="active/unactive"
					controller={{ control: form.control, name: 'mentibotEnabled' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.evaluationSurveyReminders')}
					value={programData?.surveyNotifications}
					type="boolean"
					transformValue="active/unactive"
					controller={{ control: form.control, name: 'surveyNotifications' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.calendarApiIntegration')}
					value={programData?.calendarApiIntegration}
					type="boolean"
					transformValue="active/unactive"
					controller={{ control: form.control, name: 'calendarApiIntegration' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.requireTwoFactorAuth')}
					value={programData?.requireTwoFactorAuth}
					type="boolean"
					transformValue="yes/no"
					controller={{ control: form.control, name: 'requireTwoFactorAuth' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.additionalSettings.brandColor')}
					value={programData?.brandColor}
					type="color"
					withEmptyText="default"
					controller={{ control: form.control, name: 'brandColor' }}
				/>
			</>
		</Panel>
	);
};
